import React, { useState, useEffect } from 'react';

import { propertyApi, type PropertyData } from '../services';
import { useToast } from '../contexts/ToastContext';
import HouseIcon from '../assets/house.svg';
import EuroIcon from '../assets/euro.svg';
import CashIcon from '../assets/cash.svg';
import GraphIcon from '../assets/graph-icon.svg';
import './DashboardPage.css';
import PolygonUp from '../components/PolygonUp';
import PolygonDown from '../components/PolygonDown';
import PortfolioEvaluationChart from '../components/PortfolioEvaluationChart';

const DashboardPage: React.FC = () => {
  const { showError } = useToast();
  const [properties, setProperties] = useState<PropertyData[]>([]);
  const [showTransactions, setShowTransactions] = useState(false);
  const [timePeriod, setTimePeriod] = useState<
    '6months' | '12months' | '5years' | 'all'
  >('12months');

  // Helper function to normalize property data from API (convert string numbers to actual numbers)
  const normalizeProperty = (prop: PropertyData): PropertyData => {
    return {
      ...prop,
      kaufpreis:
        prop.kaufpreis !== undefined && prop.kaufpreis !== null
          ? typeof prop.kaufpreis === 'string'
            ? parseFloat(prop.kaufpreis)
            : prop.kaufpreis
          : undefined,
      currentValue:
        prop.currentValue !== undefined && prop.currentValue !== null
          ? typeof prop.currentValue === 'string'
            ? parseFloat(prop.currentValue)
            : prop.currentValue
          : undefined,
      eigenkapital:
        prop.eigenkapital !== undefined && prop.eigenkapital !== null
          ? typeof prop.eigenkapital === 'string'
            ? parseFloat(prop.eigenkapital)
            : prop.eigenkapital
          : undefined,
      fremdkapital:
        prop.fremdkapital !== undefined && prop.fremdkapital !== null
          ? typeof prop.fremdkapital === 'string'
            ? parseFloat(prop.fremdkapital)
            : prop.fremdkapital
          : undefined,
      monatlicheRate:
        prop.monatlicheRate !== undefined && prop.monatlicheRate !== null
          ? typeof prop.monatlicheRate === 'string'
            ? parseFloat(prop.monatlicheRate)
            : prop.monatlicheRate
          : undefined,
      zinssatz:
        prop.zinssatz !== undefined && prop.zinssatz !== null
          ? typeof prop.zinssatz === 'string'
            ? parseFloat(prop.zinssatz)
            : prop.zinssatz
          : undefined,
      area:
        prop.area !== undefined && prop.area !== null
          ? typeof prop.area === 'string'
            ? parseFloat(prop.area)
            : prop.area
          : undefined,
      gain: typeof prop.gain === 'string' ? parseFloat(prop.gain) : prop.gain,
      gainPercentage:
        typeof prop.gainPercentage === 'string'
          ? parseFloat(prop.gainPercentage)
          : prop.gainPercentage,
    };
  };

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const data = await propertyApi.getAll();
        // Normalize all properties to ensure numeric fields are numbers
        const normalizedData = data.map(normalizeProperty);
        setProperties(normalizedData);
      } catch (error: any) {
        showError(error.message || 'Failed to load properties');
      }
    };

    fetchProperties();
  }, []);

  // Filter out watchlist properties (those without a kaufdatum)
  const ownedProperties = properties.filter(
    (prop) => prop.kaufdatum && prop.kaufpreis,
  );

  // Active owned properties (excluding sold properties for current debt/rates)
  const activeOwnedProperties = ownedProperties.filter(
    (prop) => !prop.sellDate,
  );

  // Calculate statistics from owned properties
  const totalProperties = ownedProperties.length;

  const portfolioValue = ownedProperties.reduce((sum, prop) => {
    if (prop.sellDate && prop.sellAmount) {
      return sum + (prop.sellAmount - (prop.kaufpreis || 0)); // sold (gain)
    }
    return sum + (prop.currentValue || 0); // bought (property value)
  }, 0);

  const totalGain = ownedProperties.reduce((sum, prop) => {
    if (prop.sellDate && prop.sellAmount) {
      return sum + (prop.sellAmount - (prop.kaufpreis || 0));
    }
    return sum + (prop.gain || 0);
  }, 0);

  // Calculate financial statistics for active portfolio
  const totalEigenkapital = activeOwnedProperties.reduce((sum, prop) => {
    if (prop.fremdfinanziert) {
      if (prop.eigenkapital !== undefined && prop.eigenkapital !== null) {
        return sum + prop.eigenkapital;
      }
      if (prop.kaufpreis !== undefined && prop.fremdkapital !== undefined) {
        return sum + Math.max(0, prop.kaufpreis - prop.fremdkapital);
      }
    }
    return sum + (prop.kaufpreis || 0);
  }, 0);

  const totalFremdkapital = activeOwnedProperties.reduce((sum, prop) => {
    if (prop.fremdfinanziert) {
      if (prop.fremdkapital !== undefined && prop.fremdkapital !== null) {
        return sum + prop.fremdkapital;
      }
      if (prop.kaufpreis !== undefined && prop.eigenkapital !== undefined) {
        return sum + Math.max(0, prop.kaufpreis - prop.eigenkapital);
      }
    }
    return sum;
  }, 0);

  const totalMonatlicheFixkosten = activeOwnedProperties.reduce((sum, prop) => {
    if (prop.fremdfinanziert && prop.monatlicheRate) {
      return sum + prop.monatlicheRate;
    }
    return sum;
  }, 0);

  const totalKaufpreisActive = activeOwnedProperties.reduce(
    (sum, prop) => sum + (prop.kaufpreis || 0),
    0,
  );

  const fremdkapitalquote =
    totalKaufpreisActive > 0
      ? (totalFremdkapital / totalKaufpreisActive) * 100
      : 0;

  // Calculate area and sqm price statistics for active portfolio
  const totalArea = activeOwnedProperties.reduce((sum, prop) => {
    return sum + (prop.area || 0);
  }, 0);

  const activePortfolioValue = activeOwnedProperties.reduce((sum, prop) => {
    return sum + (prop.currentValue || 0);
  }, 0);

  const avgPricePerSqm = totalArea > 0 ? activePortfolioValue / totalArea : 0;

  // Calculate gain percentage for portfolio
  const totalPurchasePrice = ownedProperties.reduce(
    (sum, prop) => sum + (prop.kaufpreis || 0),
    0,
  );
  const portfolioGainPercentage =
    totalPurchasePrice > 0 ? (totalGain / totalPurchasePrice) * 100 : 0;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatGain = (gain: number) => {
    const sign = gain >= 0 ? '+' : '-';
    return `${sign}${formatCurrency(gain)}`;
  };

  return (
    <div className="dashboard-page">
      <header className="dashboard-header">
        <h2>Portfolioübersicht</h2>
      </header>

      <main className="dashboard-content">
        <div className="dashboard-left">
          <div className="card-header-row">
            <div className="card-header">Gesamtwert & Wertentwicklung</div>
            <div className="card-icon">
              <img src={GraphIcon} alt="" className="graph-icon" />
            </div>
          </div>

          <div className="card-content-row-split">
            <div className="card-split-content">
              <div className="card-subtitle">Geschätzter Gesamtwert</div>
              <div className="card-value">
                <div className="card-value-split">
                  <div className="card-value-split-content">
                    {formatCurrency(portfolioValue)}
                  </div>
                  <div className="card-value-split-content">
                    <div className="card-value-gain">
                      {portfolioGainPercentage >= 0 ? (
                        <PolygonUp />
                      ) : (
                        <PolygonDown />
                      )}
                      {portfolioGainPercentage.toFixed(2)} %
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-split-content">
              <div className="card-subtitle">Geschätzte Wertentwicklung</div>
              <div
                className={`card-value ${totalGain >= 0 ? 'card-value-positive' : 'card-value-negative'}`}
              >
                {formatGain(totalGain)}
              </div>
            </div>
          </div>

          <div className="portfolio-development-graph">
            <div className="portfolio-development-header">
              <div className="portfolio-development-timeframe">
                <div
                  className={
                    timePeriod === '6months'
                      ? 'portfolio-development-timeframe-selector-active'
                      : 'portfolio-development-timeframe-selector'
                  }
                  onClick={() => setTimePeriod('6months')}
                >
                  6 Monate
                </div>
                <div
                  className={
                    timePeriod === '12months'
                      ? 'portfolio-development-timeframe-selector-active'
                      : 'portfolio-development-timeframe-selector'
                  }
                  onClick={() => setTimePeriod('12months')}
                >
                  1 Jahr
                </div>
                <div
                  className={
                    timePeriod === 'all'
                      ? 'portfolio-development-timeframe-selector-active'
                      : 'portfolio-development-timeframe-selector'
                  }
                  onClick={() => setTimePeriod('all')}
                >
                  Max.
                </div>
              </div>

              <div className="portfolio-development-transactions-toggle">
                <span className="portfolio-development-toggle-label">
                  Transaktionen
                </span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={showTransactions}
                    onChange={(e) => setShowTransactions(e.target.checked)}
                  />
                  <span className="slider round" />
                </label>
              </div>
            </div>
            <div className="portfolio-development-graph-content">
              <PortfolioEvaluationChart
                properties={properties}
                showTransactions={showTransactions}
                timePeriod={timePeriod}
              />
            </div>
          </div>
        </div>
        <div className="dashboard-right">
          <div className="dashboard-card-row">
            <div className="dashboard-card dashboard-card-2">
              <div className="card-header-row">
                <div className="card-header">Gesamtzahl der Immobilien</div>
                <div className="card-icon">
                  <img src={HouseIcon} alt="" className="house-icon" />
                </div>
              </div>
              <div className="card-content">
                <div className="card-subtitle">Immobilien im Portfolio</div>
                <div className="card-value">{totalProperties}</div>
              </div>
            </div>

            <div className="dashboard-card dashboard-card-2">
              <div className="card-header-row">
                <div className="card-header">Gesamtrendite</div>
                <div className="card-icon">
                  <img src={EuroIcon} alt="" className="euro-icon" />
                </div>
              </div>
              <div className="card-content">
                <div className="card-subtitle">
                  Wertsteigerung und Mieteinnahmen
                </div>
                <div
                  className={`card-value ${totalGain >= 0 ? 'card-value-positive' : 'card-value-negative'}`}
                >
                  {formatGain(totalGain)}
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-card-row">
            <div className="dashboard-card dashboard-card-1">
              <div className="card-header-row">
                <div className="card-header">Finanzen</div>
                <div className="card-icon">
                  <img src={CashIcon} alt="" className="cash-icon" />
                </div>
              </div>
              <div className="card-content-row-split">
                <div className="card-split-content">
                  <div className="card-subtitle">Eigenkapital</div>
                  <div className="card-value">
                    {formatCurrency(totalEigenkapital)}
                  </div>
                </div>
                <div className="card-split-content">
                  <div className="card-subtitle">Fremdkapital</div>
                  <div className="card-value">
                    {formatCurrency(totalFremdkapital)}
                  </div>
                </div>
              </div>
              <div className="card-content-row-split">
                <div className="card-split-content">
                  <div className="card-subtitle">Monatliche Fixkosten</div>
                  <div className="card-value">
                    {formatCurrency(totalMonatlicheFixkosten)}
                  </div>
                </div>
                <div className="card-split-content">
                  <div className="card-subtitle">Fremdkapitalquote</div>
                  <div className="card-value">
                    {fremdkapitalquote.toFixed(1).replace('.', ',')} %
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-card-row">
            <div className="dashboard-card dashboard-card-1">
              <div className="card-header-row">
                <div className="card-header">
                  Fläche &amp; Quadratmeterpreis
                </div>
                <div className="card-icon">
                  <img src={HouseIcon} alt="" className="house-icon" />
                </div>
              </div>
              <div className="card-content-row-split">
                <div className="card-split-content">
                  <div className="card-subtitle">Gesamte Wohnfläche</div>
                  <div className="card-value">
                    {totalArea > 0
                      ? `${totalArea.toLocaleString('de-DE')} m²`
                      : '0 m²'}
                  </div>
                </div>
                <div className="card-split-content">
                  <div className="card-subtitle">Ø Aktueller m²-Preis</div>
                  <div className="card-value">
                    {totalArea > 0
                      ? `${formatCurrency(avgPricePerSqm)} / m²`
                      : '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
